<template>
<div>
    <!-- HERO -->
    <section class="pt-12 pt-md-14 mt-n11 bg-primary">
        <div class="container">
            <div class="row align-items-center">
                <div class="col-12 col-md-8">
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb breadcrumb-dark mb-2">
                            <li class="breadcrumb-item"><router-link to="/docs" class="text-white-80">Docs</router-link></li>
                            <li class="breadcrumb-item active text-white" aria-current="page">Cache API</li>
                        </ol>
                    </nav>
                    <h1 class="display-3 fw-bold text-white">Cache API Documentation</h1>
                    <p class="lead text-white-80 mb-0">
                        REST API for accessing cached Instagram data including profiles, posts, stories, and more.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- CONTENT -->
    <section class="py-8 py-md-11">
        <div class="container">
            <div class="row">
                <!-- SIDEBAR NAV -->
                <div class="col-12 col-lg-3 mb-8 mb-lg-0">
                    <div class="docs-nav-sidebar">
                        <h6 class="text-uppercase text-muted mb-3">On this page</h6>
                        <nav class="nav flex-column">
                            <a href="#overview" class="nav-link px-0">Overview</a>
                            <a href="#architecture" class="nav-link px-0">Architecture</a>
                            <a href="#authentication" class="nav-link px-0">Authentication</a>
                            <a href="#endpoints" class="nav-link px-0">Endpoints</a>
                            <a href="#examples" class="nav-link px-0">Code Examples</a>
                            <a href="#rate-limits" class="nav-link px-0">Rate Limits</a>
                            <a href="#errors" class="nav-link px-0">Error Codes</a>
                        </nav>
                    </div>
                </div>

                <!-- MAIN CONTENT -->
                <div class="col-12 col-lg-9">
                    <!-- OVERVIEW -->
                    <div id="overview" class="docs-section">
                        <h2 class="fw-bold">Overview</h2>
                        <p class="text-muted fs-lg">
                            Cache API provides fast access to Instagram public data through a RESTful interface.
                            Data is stored in our database and updated regularly, ensuring quick response times
                            while reducing load on Instagram's servers.
                        </p>
                        <div class="row mt-4">
                            <div class="col-12 col-md-4 mb-3">
                                <div class="d-flex align-items-center">
                                    <div class="badge bg-success-soft text-success me-3">
                                        <i class="fe fe-check"></i>
                                    </div>
                                    <span class="text-muted">User profiles</span>
                                </div>
                            </div>
                            <div class="col-12 col-md-4 mb-3">
                                <div class="d-flex align-items-center">
                                    <div class="badge bg-success-soft text-success me-3">
                                        <i class="fe fe-check"></i>
                                    </div>
                                    <span class="text-muted">Posts & Reels</span>
                                </div>
                            </div>
                            <div class="col-12 col-md-4 mb-3">
                                <div class="d-flex align-items-center">
                                    <div class="badge bg-success-soft text-success me-3">
                                        <i class="fe fe-check"></i>
                                    </div>
                                    <span class="text-muted">Stories & Highlights</span>
                                </div>
                            </div>
                            <div class="col-12 col-md-4 mb-3">
                                <div class="d-flex align-items-center">
                                    <div class="badge bg-success-soft text-success me-3">
                                        <i class="fe fe-check"></i>
                                    </div>
                                    <span class="text-muted">Comments</span>
                                </div>
                            </div>
                            <div class="col-12 col-md-4 mb-3">
                                <div class="d-flex align-items-center">
                                    <div class="badge bg-success-soft text-success me-3">
                                        <i class="fe fe-check"></i>
                                    </div>
                                    <span class="text-muted">Hashtags</span>
                                </div>
                            </div>
                            <div class="col-12 col-md-4 mb-3">
                                <div class="d-flex align-items-center">
                                    <div class="badge bg-success-soft text-success me-3">
                                        <i class="fe fe-check"></i>
                                    </div>
                                    <span class="text-muted">Locations</span>
                                </div>
                            </div>
                            <div class="col-12 col-md-4 mb-3">
                                <div class="d-flex align-items-center">
                                    <div class="badge bg-success-soft text-success me-3">
                                        <i class="fe fe-check"></i>
                                    </div>
                                    <span class="text-muted">Audio Tracks</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ARCHITECTURE -->
                    <div id="architecture" class="docs-section">
                        <h2 class="fw-bold">Architecture</h2>
                        <p class="text-muted">
                            Cache API uses a two-tier architecture: requests first check our database cache,
                            then fall back to <a href="https://hikerapi.com" target="_blank" rel="noopener">HikerAPI</a> if not found.
                        </p>
                        <div class="docs-diagram mt-4">
                            <pre class="mb-0 text-white-80">
┌─────────────────┐      ┌───────────────────────────────────────┐
│  Your App       │      │           Cache API                   │
│                 │      │                                       │
│  GET /v1/user   │─────▶│  ┌─────────────┐   ┌──────────────┐  │
│    /by/username │      │  │             │   │              │  │
│                 │      │  │  Database   │   │  HikerAPI    │  │
│                 │◀─────│  │  (Cache)    │──▶│  (Fallback)  │  │
│   JSON Response │      │  │             │   │              │  │
│                 │      │  └─────────────┘   └──────────────┘  │
└─────────────────┘      └───────────────────────────────────────┘

                         Response Flow:
                         1. Check database cache
                         2. If found → return cached data
                         3. If not found → fetch from HikerAPI
                         4. Store in cache → return fresh data</pre>
                        </div>
                        <div class="alert alert-primary mt-4">
                            <strong>Data Freshness:</strong> Cached data is typically 1-7 days old.
                            For real-time data, consider using Dataset Builder with fresh collection.
                        </div>
                    </div>

                    <!-- AUTHENTICATION -->
                    <div id="authentication" class="docs-section">
                        <h2 class="fw-bold">Authentication</h2>
                        <p class="text-muted">
                            All API requests require authentication using an access token. You can pass the token
                            in two ways:
                        </p>

                        <h5 class="mt-4">Option 1: HTTP Header (Recommended)</h5>
                        <div class="docs-code-block">
                            <pre><code>x-access-key: YOUR_ACCESS_TOKEN</code></pre>
                        </div>

                        <h5 class="mt-4">Option 2: Query Parameter</h5>
                        <div class="docs-code-block">
                            <pre><code>GET /v1/user/by/username?username=nike&access_key=YOUR_ACCESS_TOKEN</code></pre>
                        </div>

                        <div class="card bg-dark border-0 mt-4">
                            <div class="card-body">
                                <h6 class="text-white">Get Your Token</h6>
                                <p class="text-muted small mb-3">
                                    Create and manage access tokens in your Console
                                </p>
                                <router-link :to="{path: '/tokens'}" class="btn btn-sm btn-primary">
                                    Go to Tokens <i class="fe fe-arrow-right ms-2"></i>
                                </router-link>
                            </div>
                        </div>
                    </div>

                    <!-- ENDPOINTS -->
                    <div id="endpoints" class="docs-section">
                        <h2 class="fw-bold">Endpoints</h2>
                        <p class="text-muted mb-4">
                            Base URL: <code>https://api.{{ $state.siteDomain }}/v1</code>
                        </p>

                        <!-- Users -->
                        <h4 class="mt-5 mb-3">Users</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Endpoint</th>
                                        <th>Method</th>
                                        <th>Description</th>
                                        <th>Parameters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>/user/by/username</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get user by username</td>
                                        <td><code>username</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>/user/by/id</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get user by Instagram ID</td>
                                        <td><code>id</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>/user/about</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get extended about info (country, registration date, former usernames)</td>
                                        <td><code>id</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>/user/face</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get AI face analysis from profile picture</td>
                                        <td><code>id</code></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Media -->
                        <h4 class="mt-5 mb-3">Media (Posts & Reels)</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Endpoint</th>
                                        <th>Method</th>
                                        <th>Description</th>
                                        <th>Parameters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>/media/by/id</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get media by ID</td>
                                        <td><code>id</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>/media/by/code</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get media by shortcode</td>
                                        <td><code>code</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>/media/by/url</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get media by full Instagram URL</td>
                                        <td><code>url</code></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Stories -->
                        <h4 class="mt-5 mb-3">Stories</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Endpoint</th>
                                        <th>Method</th>
                                        <th>Description</th>
                                        <th>Parameters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>/story/by/id</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get story by ID</td>
                                        <td><code>id</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>/story/by/url</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get story by URL</td>
                                        <td><code>url</code></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Highlights -->
                        <h4 class="mt-5 mb-3">Highlights</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Endpoint</th>
                                        <th>Method</th>
                                        <th>Description</th>
                                        <th>Parameters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>/highlight/by/id</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get highlight by ID</td>
                                        <td><code>id</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>/highlight/by/url</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get highlight by URL</td>
                                        <td><code>url</code></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Comments -->
                        <h4 class="mt-5 mb-3">Comments</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Endpoint</th>
                                        <th>Method</th>
                                        <th>Description</th>
                                        <th>Parameters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>/comment/by/id</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get comment by ID (cache only)</td>
                                        <td><code>id</code></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Hashtags -->
                        <h4 class="mt-5 mb-3">Hashtags</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Endpoint</th>
                                        <th>Method</th>
                                        <th>Description</th>
                                        <th>Parameters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>/hashtag/by/id</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get hashtag by ID</td>
                                        <td><code>id</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>/hashtag/by/name</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get hashtag by name</td>
                                        <td><code>name</code></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Locations -->
                        <h4 class="mt-5 mb-3">Locations</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Endpoint</th>
                                        <th>Method</th>
                                        <th>Description</th>
                                        <th>Parameters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>/location/by/id</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get location by ID</td>
                                        <td><code>id</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>/location/by/name</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Search location by name</td>
                                        <td><code>name</code></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Tracks -->
                        <h4 class="mt-5 mb-3">Audio Tracks</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Endpoint</th>
                                        <th>Method</th>
                                        <th>Description</th>
                                        <th>Parameters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>/track/by/id</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get audio track by ID</td>
                                        <td><code>id</code></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- System -->
                        <h4 class="mt-5 mb-3">System</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Endpoint</th>
                                        <th>Method</th>
                                        <th>Description</th>
                                        <th>Parameters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>/sys/healthcheck</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Check API health</td>
                                        <td>-</td>
                                    </tr>
                                    <tr>
                                        <td><code>/sys/balance</code></td>
                                        <td><span class="badge bg-success">GET</span></td>
                                        <td>Get account balance and usage</td>
                                        <td>-</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- CODE EXAMPLES -->
                    <div id="examples" class="docs-section">
                        <h2 class="fw-bold">Code Examples</h2>
                        <p class="text-muted mb-4">
                            Examples of API requests in various programming languages
                        </p>
                        <CodeExamples />
                    </div>

                    <!-- RATE LIMITS -->
                    <div id="rate-limits" class="docs-section">
                        <h2 class="fw-bold">Rate Limits</h2>
                        <p class="text-muted">
                            Rate limits depend on your subscription plan. Exceeding limits returns HTTP 429.
                        </p>
                        <div class="table-responsive mt-4">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Plan</th>
                                        <th>Requests/minute</th>
                                        <th>Requests/day</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Trial</td>
                                        <td>10</td>
                                        <td>100</td>
                                    </tr>
                                    <tr>
                                        <td>Basic</td>
                                        <td>60</td>
                                        <td>10,000</td>
                                    </tr>
                                    <tr>
                                        <td>Pro</td>
                                        <td>120</td>
                                        <td>50,000</td>
                                    </tr>
                                    <tr>
                                        <td>Enterprise</td>
                                        <td>Custom</td>
                                        <td>Custom</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p class="text-muted mt-3">
                            <router-link :to="{path: '/pricing'}">View all plans</router-link> for detailed pricing.
                        </p>
                    </div>

                    <!-- ERROR CODES -->
                    <div id="errors" class="docs-section">
                        <h2 class="fw-bold">Error Codes</h2>
                        <p class="text-muted mb-4">
                            API errors are returned as JSON with an error code and message.
                        </p>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>HTTP Code</th>
                                        <th>Error</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>400</code></td>
                                        <td>Bad Request</td>
                                        <td>Missing or invalid parameters</td>
                                    </tr>
                                    <tr>
                                        <td><code>401</code></td>
                                        <td>Unauthorized</td>
                                        <td>Invalid or missing access token</td>
                                    </tr>
                                    <tr>
                                        <td><code>403</code></td>
                                        <td>Forbidden</td>
                                        <td>Insufficient permissions or plan limits</td>
                                    </tr>
                                    <tr>
                                        <td><code>404</code></td>
                                        <td>Not Found</td>
                                        <td>Resource not found (user, media, etc.)</td>
                                    </tr>
                                    <tr>
                                        <td><code>429</code></td>
                                        <td>Too Many Requests</td>
                                        <td>Rate limit exceeded</td>
                                    </tr>
                                    <tr>
                                        <td><code>500</code></td>
                                        <td>Internal Error</td>
                                        <td>Server error, please retry</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h5 class="mt-4">Example Error Response</h5>
                        <div class="docs-code-block">
                            <pre><code>{
  "detail": "User not found",
  "exc_type": "UserNotFound"
}</code></pre>
                        </div>
                    </div>

                    <!-- NEXT STEPS -->
                    <div class="mt-5 pt-5 border-top border-secondary">
                        <h4>Next Steps</h4>
                        <div class="row mt-4">
                            <div class="col-12 col-md-6 mb-3">
                                <router-link :to="{path: '/docs/mcp'}" class="card bg-dark border-0 h-100 text-decoration-none lift">
                                    <div class="card-body">
                                        <h6 class="text-white mb-1">MCP Server</h6>
                                        <p class="text-muted small mb-0">Use AI to query Instagram data</p>
                                    </div>
                                </router-link>
                            </div>
                            <div class="col-12 col-md-6 mb-3">
                                <router-link :to="{path: '/docs/datasets'}" class="card bg-dark border-0 h-100 text-decoration-none lift">
                                    <div class="card-body">
                                        <h6 class="text-white mb-1">Datasets</h6>
                                        <p class="text-muted small mb-0">Download bulk data in CSV</p>
                                    </div>
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</div>
</template>

<script>
import CodeExamples from './CodeExamples.vue'

export default {
    components: { CodeExamples },
    created() {
        this.$methods.buildTitle('Cache API Documentation')
    },
    mounted() {
        // Handle hash navigation
        if (this.$route.hash) {
            this.$nextTick(() => {
                const element = document.querySelector(this.$route.hash)
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' })
                }
            })
        }
    }
}
</script>
